import { kea } from 'kea'
import PropTypes from 'prop-types'
import {select, put, call, apply, all} from 'redux-saga/effects'
import {reduce, get, filter, map, findIndex, forEach, find, omit} from 'lodash'
import entities from '~/entities'
import {
  Applications,
  ShopPaymentMethods,
  ShopShippingMethods,
  Shops,
  Carts,
  Orders,
  CartItems,
  Products
} from '@gratify-eu/entities'

import i18n, { __ } from '~/i18n'
import { selector as configSelector, actions as configActions } from '~/config'
import { STATE_CONTENTS, STATE_HIDDEN, STATE_ORDERED } from '~/constants/cart'
import { TAX_VISIBILITY_HIDDEN, TAX_VISIBILITY_MESSAGE_ONLY } from '~/constants/shop'
import { getAccountsEnabled } from '~/logic'
import { JWTApi as API } from '@gratify-eu/api-client'

export default kea({
  path: () => ['scenes', 'root'],

  actions: ({ constants }) => ({
    closeCart: true,
    openCart: true,
    openCartComplete: true,
    next: true,
    previous: true,
    navigate: (step, action) => ({step, action}),
    setLoggedIn: true,
    setLoggedOut: true,
    setLoading: true,
    clearLoading: true,
    placeOrderSuccess: true,
    retryPayment: (url) => ({url}),
    cancelPayment: true,
    setUserInfo: (userInfo) => ({userInfo}),
    auth0Authenticated: (authResult) => ({authResult}),
    logout: true,
  }),

  reducers: ({ actions, constants }) => ({
    openingCart: [false, PropTypes.bool, {
      [actions.openCart]: () => true,
      [actions.openCartComplete]: () => false,
    }],
    loggedIn: [false, PropTypes.bool, {
      [actions.setLoggedIn]: () => true,
      [actions.setLoggedOut]: () => false,
    }],
    loading: [false, PropTypes.bool, {
      [actions.setLoading]: () => true,
      [actions.clearLoading]: () => false,
    }],
    userInfo: [null, PropTypes.object, {
      [actions.setUserInfo]: (_, payload) => (payload.userInfo),
      [actions.setLoggedOut]: () => null,
    }],
    retrying: [false, PropTypes.bool, {
      [actions.retryPayment]: () => true,
      [actions.cancelPayment]: () => false,
      [configActions.clearActiveCart]: () => false,
    }],
    cancelled: [false, PropTypes.bool, {
      [actions.cancelPayment]: () => true,
      [actions.retryPayment]: () => false,
      [configActions.clearActiveCart]: () => false,
    }],
    paymentUrl: [null, PropTypes.string, {
      [actions.retryPayment]: (_, payload) => (payload.url),
      [configActions.clearActiveCart]: () => null,
    }]
  }),

  selectors : ({ constants, selectors }) => ({
    cartVisible: [
      () => [
        entities.selectors.activeCart
      ], (activeCart) => {
        if (activeCart) {
          return activeCart.current_state === STATE_CONTENTS
        }

        return false;
      },
      PropTypes.bool
    ],
    cartButtonVisible: [
      () => [
        entities.selectors.activeCart,
        configSelector
      ], (activeCart, config) => {
        if(activeCart) {
          return activeCart.current_state === STATE_HIDDEN && activeCart.contents && activeCart.contents.length > 0 && config.cartFloaterEnabled
        }
        return false;
      },
      PropTypes.bool
    ],
    cartButtonAlignment: [
      () => [
        configSelector
      ], (config) => {
        return config.cartFloatAlignment;
      },
      PropTypes.string
    ],
    cartItemsCount: [
      () => [
        entities.selectors.activeCart,
      ], (activeCart) => {
        if(activeCart) {
          return reduce(activeCart.contents, (sum, item) => {
            return sum + item.quantity;
          }, 0)
        }

        return null;
      },
      PropTypes.number
    ],

    checkoutVisible: [
      () => [
        entities.selectors.activeCart
      ], (activeCart) => {
        if (activeCart) {
          return activeCart.current_state !== STATE_CONTENTS && activeCart.current_state !== STATE_HIDDEN
        }

        return false;
      },
      PropTypes.bool
    ],

    nextStep: [
      () => [
        entities.selectors.activeCart,
      ], (activeCart) => {
        if(activeCart) {
          const { routes } = activeCart;
          const currentIndex = findIndex(routes, {to: activeCart.current_state});
          const nextStep = routes[currentIndex+1];

          if(nextStep && nextStep.name !== 'hide') {
            return nextStep;
          }
        }

        return null;
      },
      PropTypes.object
    ],

    previousStep: [
      () => [
        entities.selectors.activeCart,
      ], (activeCart) => {
        if(activeCart) {
          const { routes } = activeCart;
          const currentIndex = findIndex(routes, {to: activeCart.current_state});
          if(currentIndex-1 >= 0) {
            const previousStep = routes[currentIndex-1];

            if(previousStep) {
              return previousStep;
            }
          }
        }

        return null;
      },
      PropTypes.object
    ],

    availableSteps: [
      () => [
        entities.selectors.activeCart,
      ], (activeCart) => {
        if(activeCart) {
          const { routes } = activeCart;
          const currentState = activeCart.current_state;

          return map(filter(routes, (route) => {
            return route.from.indexOf(currentState) > -1
          }), (route) => route.to);
        }

        return [];
      },
      PropTypes.array
    ],

    auth0Settings: [
      () => [
        entities.selectors.activeShop,
      ], (activeShop) => {
        return get(activeShop, 'checkout_settings.auth0_settings', null);
      },
      PropTypes.object
    ],

    hasActiveDiscounts: [
      () => [
        entities.selectors.activeShop,
      ], (activeShop) => {
        return get(activeShop, 'checkout_settings.has_active_discounts', false);
      },
      PropTypes.bool
    ],

    shippingRequired: [
      () => [
        entities.selectors.activeCart
      ], (activeCart) => {
        if(activeCart) {
          let shippingRequired = false;
          forEach(activeCart.contents, (val) => {
            if(val && val.shipping_required) {
              shippingRequired = true;
              return false;
            }
          });
          return shippingRequired
        }

        return false;
      },
      PropTypes.bool
    ],

    orderPaymentMethod: [
      () => [
        entities.selectors.activeOrder,
        entities.selectors.shopPaymentMethods,
      ], (activeOrder, shopPaymentMethods) => {
        if(activeOrder && activeOrder.payment_method) {
          const paymentMethod = shopPaymentMethods[activeOrder.payment_method];

          if(paymentMethod) {
            return paymentMethod
          }
        }
        return null;
      },
      PropTypes.object,
    ]
  }),

  start: function * () {
    console.log('start root');
    yield call(this.workers.initWorker);
    yield call(this.workers.initAccount);
  },

  takeEvery: ({actions, workers}) => ({
    [Carts.actions.create.success]: function * (action) {
      const { cart } = action.payload || {};

      if(cart && cart.id) {
        yield put(configActions.setCartId(cart.id))
      }
    }
  }),

  takeLatest: ({actions, workers}) => ({
    [actions.closeCart]: workers.closeCart,
    [actions.openCart]: workers.openCart,
    [actions.next]: workers.continue,
    [actions.previous]: workers.previous,
    [actions.placeOrderSuccess]: workers.orderPaymentWorker,
    [Carts.actions.create.success]: workers.cartUpdated,
    [Carts.actions.create.failure]: workers.createCartFailure,
    [CartItems.actions.update.success]: workers.cartUpdated,
    [CartItems.actions.create.success]: workers.cartUpdated,
    [actions.auth0Authenticated]: workers.auth0Authenticated,
    [actions.logout]: workers.logout,
    [actions.navigate]: workers.navigate,
    [configActions.addItemsToCart]: workers.addItemsToCart,
  }),

  workers: {
    initWorker: function * () {
      try {
        const config = yield select(configSelector);
        const { appId, shopId, cartId, requestedLanguage } = config;
        const { setActiveShopId, setDefaultLanguage, setLanguage } = configActions;

        if(!appId) {
          return null;
        }

        const app = yield call(Applications.workers.fetch, {
          payload: { id: appId }
        }, true);

        if(app) {
          const { shop } = app;

          if(shop) {
            let clientLanguage = config.language;
            const paymentMethods = shop.payment_methods;
            const shippingMethods = shop.shipping_methods;
            const checkoutSettings = shop.checkout_settings;

            yield put(Shops.actions.fetch.success(shop));
            yield put(ShopPaymentMethods.actions.fetchList.success(paymentMethods));
            yield put(ShopShippingMethods.actions.fetchList.success(shippingMethods));
            yield put(setActiveShopId(shop.id));
            yield put(setDefaultLanguage(checkoutSettings.default_language));
            if(requestedLanguage) {
              const languages = checkoutSettings.languages || [];
              const language = languages.indexOf(requestedLanguage) > -1 ? requestedLanguage : find(languages, (lang) => {
                return lang.substr(0, 2) === requestedLanguage.substr(0, 2);
              });

              console.log('lang', language);
              if(language) {
                yield put(setLanguage(language));
                clientLanguage = language;
              }
            }

            i18n.changeLanguage(clientLanguage || checkoutSettings.default_language || 'et');
          }
        }

        if(cartId) {
          try {
            const cart = yield call(Carts.workers.fetch, Carts.actions.fetch.request(cartId), true);

            if(cart.state === 'ordered') {
              if(cart.state === STATE_ORDERED) {
                try {
                  const orders = yield call(Orders.workers.fetchList, Orders.actions.fetchList.request({cart: cart.id}));

                  if(orders[0].id) {
                    yield call(this.workers.orderPaymentWorker, false);
                  }
                } catch(e) {
                  // TODO: handle when order is not received
                }
              }
            } else {
              // TODO: handle parsing of discount code
            }
          } catch(e) {
            yield put(configActions.clearActiveCart())
          }
        }
      } catch(e) {

      }
    },
    closeCart: function * () {
      const { setLoading, clearLoading } = this.actionCreators;
      const activeCart = yield entities.get('activeCart');
      const config = yield select(configSelector);

      if(activeCart) {
        if(activeCart.state === 'ordered') {
          yield put(configActions.clearActiveCart());
        }else {
          try {
            yield put(setLoading());
            yield call(Carts.workers.update, Carts.actions.update.request(activeCart.id, {current_state: STATE_HIDDEN}), true);
            yield put(clearLoading());
            if(config.clearCartOnClose) {
              yield put(configActions.clearActiveCart());
            }
          } catch(e) {
            yield put(clearLoading());
            console.error(e);
          }
        }

      }
    },
    openCart: function * () {
      const { openCartComplete } = this.actionCreators;
      const activeCart = yield entities.get('activeCart');

      if(activeCart && activeCart.id) {
        try {
          yield call(Carts.workers.update, Carts.actions.update.request(activeCart.id, {current_state: STATE_CONTENTS}))
        } catch(e) {
          console.error(e);
        }
      }

      yield put(openCartComplete());
    },
    continue: function * () {
      const { setLoading, clearLoading, placeOrderSuccess } = this.actionCreators;
      const activeCart = yield entities.get('activeCart');
      const nextStep = yield this.get('nextStep');

      if(activeCart && nextStep) {
        const step = nextStep.to;
        const action = nextStep.name;

        const cartAction = get(activeCart, `actions.${action}`, null);

        try {
          if(cartAction) {
            yield put(setLoading());
            const cart = yield call(Carts.workers.applyCartAction, Carts.actions.cartAction.request(cartAction), true);

            if(cart.state === STATE_ORDERED) {
              try {
                const orders = yield call(Orders.workers.fetchList, Orders.actions.fetchList.request({cart: cart.id}));

                if(orders[0].id) {
                  yield put(placeOrderSuccess())
                }
              } catch(e) {
                // TODO: handle when order is not received
              }
            }

            yield put(clearLoading());
          }else {
            const availableSteps = yield this.get('availableSteps');
            if(availableSteps.indexOf(step) > -1) {
              yield put(setLoading());
              yield call(Carts.workers.update, Carts.actions.update.request(activeCart.id, {current_state: step}), true);
              yield put(clearLoading());
            }
          }
        } catch(e) {
          yield call(this.workers.handleCartError, e);
        }
      }
    },
    previous: function * () {
      const { setLoading, clearLoading } = this.actionCreators;

      const activeCart = yield entities.get('activeCart');
      const previousStep = yield this.get('previousStep');

      if(activeCart && previousStep) {
        const step = previousStep.to;
        const action = previousStep.name;

        const cartAction = get(activeCart, `actions.${action}`, null);

        try {
          if(cartAction) {
            yield put(setLoading());
            yield call(Carts.workers.applyCartAction, Carts.actions.cartAction.request(cartAction), true);
            yield put(clearLoading());

          }else {
            const availableSteps = yield this.get('availableSteps');
            if(availableSteps.indexOf(step) > -1) {
              yield put(setLoading());
              yield call(Carts.workers.update, Carts.actions.update.request(activeCart.id, {current_state: step}), true);
              yield put(clearLoading());
            }
          }
        } catch(e) {
          yield call(this.workers.handleCartError, e);
        }

      }
    },
    orderPaymentWorker: function * (redirect = true) {
      const { retryPayment, cancelPayment } = this.actionCreators;

      try {
        const activeOrder = yield entities.get('activeOrder');
        const shopPaymentMethods = yield entities.get('shopPaymentMethods');
        const activeCart = yield entities.get('activeCart');
        if(activeOrder && shopPaymentMethods && activeCart) {
          const isPending = activeOrder.payment_status === 'pending';
          const isAuthorized = activeOrder.payment_status === 'authorized';
          const isCancelled = activeOrder.payment_status === 'cancelled';
          const isPaid = activeOrder.payment_status === 'paid';
          const isFailed = activeOrder.payment_status === 'failed';

          if (isPending || isFailed) {
            const paymentMethod = shopPaymentMethods[activeOrder.payment_method];

            if(paymentMethod) {
              if (paymentMethod.type === "ShopMaksekeskusMethod") {
                const methodName = get(activeCart, 'payment_method_arguments.name', null);
                const route = get(paymentMethod, 'routes.payment', null);
                if (methodName && route) {
                  const config = yield select(configSelector);
                  const url = `${route}?order_id=${activeOrder.id}&payment_id=${methodName}&application_id=${config.appId}`;

                  if(redirect) {
                    window.location = url;
                  }else {
                    yield put(retryPayment(url));
                  }
                }
              } else if(['ShopPayseraMethod', 'ShopGratifyMethod'].indexOf(paymentMethod.type) > -1) {
                const paymentTypeId = get(activeCart, 'payment_method_arguments.key', null);
                const confirm = get(paymentMethod, 'actions.confirm', null);

                if (paymentTypeId && confirm) {
                  const config = yield select(configSelector);
                  // const url = `${confirm}?order_id=${activeOrder.id}&payment_type_id=${paymentTypeId}&application_id=${config.appId}`;

                  try {
                    const data = yield call(
                      ShopPaymentMethods.workers.applyPaymentMethodAction,
                      ShopPaymentMethods.actions.paymentMethodAction.request(confirm, {
                        order_id: activeOrder.id,
                        payment_type_id: paymentTypeId,
                        application_id: config.appId,
                      }),
                      true
                    );

                    if(data && data.url) {
                      if(redirect) {
                        window.location = data.url;
                      }else {
                        yield put(retryPayment(data.url));
                      }
                    }
                  } catch(e) {

                  }
                }

              } else if (paymentMethod.type === "ShopPaypalMethod") {
                const route = get(paymentMethod, 'routes.payment', null);
                if (route) {
                  const config = yield select(configSelector);
                  const url = `${route}?order_id=${activeOrder.id}&application_id=${config.appId}`;

                  if(redirect) {
                    window.location = url;
                  }else {
                    yield put(retryPayment(url));
                  }
                }
              } else if (paymentMethod.type === "ShopBankDepositMethod") {

                const activeApp = yield entities.get('activeApplication');

                if(redirect) {

                  if(activeApp && activeApp.success_url) {

                    if(window.location.href !== activeApp.success_url) {
                      yield put(configActions.clearActiveCart());
                      window.location = activeApp.success_url;
                    }

                  }
                }

              } else {
                // clear active order and cart.
                yield put(configActions.clearActiveCart())
              }
            } else {
              // TODO: figure out what to do when payment method does not exist
              yield put(configActions.clearActiveCart())
            }
          } else if (isCancelled) {
            const paymentMethod = shopPaymentMethods[activeOrder.payment_method];

            if(paymentMethod) {
              if (["ShopMaksekeskusMethod", "ShopPaypalMethod", "ShopPayseraMethod", "ShopGratifyMethod"].indexOf(paymentMethod.type) > -1) {
                yield put(cancelPayment());
              } else {
                // TODO: figure out what to do when payment method does not exist
                yield put(configActions.clearActiveCart())
              }
            }
          } else if (isAuthorized || isPaid) {
            const activeApp = yield entities.get('activeApplication');
            if(activeApp && activeApp.success_url && activeApp.success_url !== activeApp.site_url) {
              yield put(configActions.clearActiveCart());
            }
          } else {
            yield put(configActions.clearActiveCart());

            if(!activeOrder.payment_method) {
              const activeApp = yield entities.get('activeApplication');

              if(redirect) {

                if(activeApp && activeApp.success_url) {

                  if(window.location.href !== activeApp.success_url) {
                    window.location = activeApp.success_url;
                  }

                }
              }
            }
          }
        }

      } catch(e) {
        // TODO: handle error
        console.log('order worker error', e);
      }
    },
    handleCartError: function * (e) {
      const { clearLoading } = this.actionCreators;
      yield put(clearLoading());
      if(e.response && e.response.status === 404) {
        yield put(configActions.clearActiveCart());
        if(typeof window !== 'undefined') {
          //TODO: figure out better way to report this error
          window.alert(__('Your cart has expired'));
        }
      }
    },
    cartUpdated: function * (action) {
      const { setLoading, clearLoading } = this.actionCreators;
      const activeCart = yield entities.get('activeCart');
      const step = 'contents';


      if(activeCart) {
        const availableSteps = yield this.get('availableSteps');
        if(availableSteps.indexOf(step) > -1) {
          yield put(Carts.actions.update.request(activeCart.id, {current_state: step}));
        }

        const hasActiveDiscounts = yield this.get('hasActiveDiscounts');
        if(activeCart.service_fee && activeCart.service_fee.amount > 0 || hasActiveDiscounts) {
          yield put(setLoading());
          yield call(Carts.workers.fetch, Carts.actions.fetch.request(activeCart.id));
          yield put(clearLoading());
        }
      }
    },
    initAccount: function * () {
      try {
        const { setUserInfo, setLoggedIn, auth0Authenticated } = this.actionCreators;
        const authConfig = yield this.get('auth0Settings');
        const activeShop = yield entities.get('activeShop');

        if(getAccountsEnabled(activeShop) && authConfig) {
          const loader = () => import('~/auth0')
          const module = yield call(loader);
          const Auth = module.default;

          const config = yield select(configSelector);
          const auth = new Auth(authConfig, config);
          const accessToken = get(config, 'userTokens.accessToken');

          const handleAuthenticated = () => {
            return new Promise(function(resolve, reject) {
              auth.lock.on('authenticated', (authResult) => {
                resolve(authResult)
              })
            });
          };

          if (accessToken) {
            const user = yield apply(auth, auth.getUserInfo, [accessToken]);
            yield put(setLoggedIn());
            yield put(setUserInfo(user));
          } else {
            const authResult = yield call(handleAuthenticated);
            yield put(auth0Authenticated(authResult));
          }
        }
      } catch (e) {
        const { setLoggedOut } = this.actionCreators;
        yield put(configActions.clearActiveCart());
        yield put(configActions.clearCustomerToken());
        yield put(configActions.clearUserTokens());
        yield put(setLoggedOut());
      }
    },
    auth0Authenticated: function * (action) {
      const { setUserInfo, setLoggedIn } = this.actionCreators;
      const { authResult } = action.payload || {};
      const { accessToken, idToken, idTokenPayload } = authResult;
      if(accessToken && idToken) {
        yield put(configActions.setUserTokens({
          accessToken,
          idToken
        }));

        yield put(setUserInfo(idTokenPayload));

        try {
          const config = yield select(configSelector);
          const api = new API(config);
          let customerToken = api.getCustomerToken();

          if(!customerToken) {
            const result = yield apply(api, api.requestToken);
            customerToken = result.token;
          }

          const response = yield apply(api, api.upgradeCustomerToken, [customerToken]);
          if(response && response.data && response.data.token) {
            yield put(configActions.setCustomerToken(response.data.token));
            yield put(setLoggedIn());
          }

          if(config.redirectAfterAuth) {
            const redirectUrl = config.redirectAfterAuth;
            yield put(configActions.clearRedirectUrl());
            if(typeof window !== 'undefined' && window.location.href !== redirectUrl) {
              window.location = redirectUrl;
            }
          }

        } catch (e) {
          console.error(e);
        }
      }
    },
    logout: function * () {
      const { setLoggedOut } = this.actionCreators;
      yield put(configActions.clearActiveCart());
      yield put(configActions.clearCustomerToken());
      yield put(configActions.clearUserTokens());
      yield put(setLoggedOut());
    },
    navigate: function * (action) {
      const { setLoading, clearLoading } = this.actionCreators;
      const { step } = action.payload || {}
      const activeCart = yield entities.get('activeCart');

      if(step && activeCart) {
        try {
          yield put(setLoading());
          yield call(Carts.workers.update, Carts.actions.update.request(activeCart.id, {current_state: step}), true);
          yield put(clearLoading());
        } catch (e) {
          console.error(e);
          yield put(clearLoading());
        }
      }
    },
    addItemsToCart: function * (action) {
      const { items } = action.payload || {};

      const addActions = map(items, (item) => {
        return call(this.workers.addItemToCart, {payload: {id: item.id}})
      });

      yield all(addActions);
    },
    addItemToCart: function * (action) {
      const { id } = action.payload || {};
      try {
        const activeCart = yield entities.get('activeCart');
        const activeShop = yield entities.get('activeShop');

        const product = yield call(Products.workers.fetch, {payload: {id: id}}, true);

        if(product) {
          if(activeCart) {
            const cartItem = find(activeCart.contents, {product: product.url});

            if(cartItem) {
              // item exists
              const updatedCartItem = {
                quantity: cartItem.quantity + 1
              };
              yield put(CartItems.actions.update.request(cartItem.id, updatedCartItem));
            }else {
              const newCartItem = {
                product: product.url,
                quantity: 1,
                cart: activeCart.url
              };
              yield put(CartItems.actions.create.request(newCartItem));
            }
          } else {
            const config  = yield select(configSelector);

            let newCartDefaults = {};
            try {
              if(config && config.cartDefaults && config.cartDefaults) {
                newCartDefaults = omit(config.cartDefaults, ['contact', 'shipping_contact']);
              }
            } catch (e) {
              console.error(e);
            }

            const newCart = {
              ...newCartDefaults,
              shop: activeShop.url,
              contents: map([product], (product) => {
                return {product: product.url, quantity: 1};
              }),
            };

            yield put(Carts.actions.create.request(newCart))
          }
        }
      } catch(e) {

      }
    },

    createCartFailure: function* (action) {
      try {
        const { response } = action.payload

        if(response.status === 401) {
          yield put(configActions.clearCustomerToken());
        }

      } catch (e) {

      }
    }
  }
})
